import * as Sentry from "@sentry/react"
import { deleteCookie } from "./common/ts-utils"
import { getAutodeskAccessTokenSilently } from "./services/autodesk-identity"
import { enableAmplitudeTracking, setAmplitudeOptOut } from "./common/amplitude-event-logging"
import { setIntercomUser, unsetIntercomUser } from "./common/intercom-event-logging"
import { getFlagEnabled } from "./getFlagValue"
import { iCurrentUser } from "./common/types"

const EVENT_GENERATOR = "rmbxcl"

// Cookie name prefixes written by the tracking integrations we gate. Removed when the
// user revokes consent, since shutting the SDKs down doesn't clear what they already wrote
// -- relevant for a mid-session revoke after a prior grant, or when cookie management is
// off and the integrations boot/cookie unconditionally at page load.
const TRACKING_COOKIE_PREFIXES = ["fs_uid", "_fs", "amplitude_", "amp_"]

// The ADP consent category that gates FullStory/Amplitude/Intercom: "Help develop new
// products and services" (usage-data-based product analytics) is the closest match to
// what those integrations actually do, as opposed to ADSK_PUD_CONTRACTUAL_NECESSITY_DESKTOP
// (fraud detection/internal ops) or ADSK_PUD_GO_TO_MARKET_DESKTOP (marketing/offers).
const TRACKING_CONSENT_ID = "ADSK_PUD_OPTIMIZATION_IMPROVEMENT_DESKTOP"

let adpConsentApi: any = null
let autodeskAccessTokenPromise: Promise<string | null> | null = null
let consentDecisionHookInstalled = false
let trackingConsentGranted = true

// The user info to identify with in Intercom once we know identification is allowed --
// stashed at login (see applyStoredConsent) since a later mid-session consent change
// (installConsentDecisionHook) needs it too, without the caller having to thread it through.
let currentUserInfo: iCurrentUser | null = null

// FullStory capture is deferred at the snippet level (window._fs_capture_on_startup, see
// rmbx_minimal.html) so no session is recorded before a consent decision is known. "start"
// only resumes a deferred-but-never-started session; a session already shut down needs
// "restart" instead -- see FullStory's Manually Delay Data Capture / Stop & Restart docs.
type FullStoryCaptureState = "not-started" | "capturing" | "shutdown"
let fullStoryCaptureState: FullStoryCaptureState = "not-started"

/**
 * Starts (or resumes) FullStory session capture. No-ops if already capturing.
 */
const startFullStoryCapture = (): void => {
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] startFullStoryCapture", { fullStoryCaptureState, hasFS: typeof window.FS === "function" })
    if (fullStoryCaptureState === "capturing") {
        return
    }
    window.FS?.(fullStoryCaptureState === "shutdown" ? "restart" : "start")
    fullStoryCaptureState = "capturing"
}

/**
 * Stops FullStory session capture. No-ops if already stopped/never started.
 */
const stopFullStoryCapture = (): void => {
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] stopFullStoryCapture", { fullStoryCaptureState, hasFS: typeof window.FS === "function" })
    if (fullStoryCaptureState === "shutdown") {
        return
    }
    window.FS?.("shutdown")
    fullStoryCaptureState = "shutdown"
}

// Amplitude is initialized with deferInitialization (see rmbx_minimal.html), so -- unlike
// FullStory -- it never needs a "shutdown"/"restart" pair: enableTracking() is a one-time
// transition out of the deferred state, and all gating after that is setAmplitudeOptOut.
let amplitudeTrackingStarted = false

/**
 * Ends Amplitude's deferred-initialization state. No-ops if already started.
 */
const startAmplitudeTracking = (): void => {
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] startAmplitudeTracking", { amplitudeTrackingStarted, hasAmplitudeKey: !!window.amplitude_key })
    if (amplitudeTrackingStarted) {
        return
    }
    enableAmplitudeTracking()
    amplitudeTrackingStarted = true
}

// Intercom starts anonymous by default (see rmbx_minimal.html/bootIntercom) so the help
// overlay still works before a consent decision is known. Unlike FullStory/Amplitude, that
// means there's nothing to defer -- only the *identify* step is gated here.
let intercomIdentified = false

/**
 * Identifies the current user in Intercom, if known. No-ops if already identified or no
 * user info has been stashed yet (see currentUserInfo).
 */
const identifyIntercomUser = (): void => {
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] identifyIntercomUser", { intercomIdentified, hasUserInfo: !!currentUserInfo })
    if (intercomIdentified || !currentUserInfo) {
        return
    }
    setIntercomUser(currentUserInfo)
    intercomIdentified = true
}

/**
 * Reverts Intercom to anonymous. No-ops if already anonymous.
 */
const anonymizeIntercomUser = (): void => {
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] anonymizeIntercomUser", { intercomIdentified })
    if (!intercomIdentified) {
        return
    }
    unsetIntercomUser()
    intercomIdentified = false
}

/**
 * Whether the Autodesk ADP consent dialog feature is enabled.
 * @returns whether Privacy Settings and the ADP/CJS Web SDK scripts are active
 */
export const isCookieManagementEnabled = (): boolean => getFlagEnabled("WA-9573-cookie-management")

/**
 * Prepares the ADP consent API for this page load. No-ops if the ADP Web SDK script
 * hasn't loaded (e.g. blocked by an extension, or the SDK URLs aren't configured for this
 * environment) or if it's already been prepared.
 *
 * Runs the ADP integration guide's required setup for the instance -- setEnv() then
 * load(productInfo) -- before anything else touches it; without both, the instance has no
 * environment/product identity and loadUserConsentPreferences/showUserConsentDialog fail.
 */
const initializeAdpConsentApi = (): void => {
    if (typeof window.Adp !== "function" || adpConsentApi) {
        return
    }
    try {
        const adp = window.Adp()
        if (typeof adp?.loadUserConsentPreferences !== "function") {
            Sentry.captureMessage("ADP Web SDK exposes no consent API; consent dialog unavailable")
            return
        }
        adpConsentApi = adp.setEnv(window.adp_cjs_env)
        // Documented to suppress the SDK's automatic SESSION_PAGE upload on load() -- see
        // adpConsent.ts history/PR discussion: confirmed present and called, but the upload
        // still fires. Left in place since it's the documented API and may still matter for
        // later auto-capture triggers (URL changes); the initial-load upload needs follow-up
        // with the CJS SDK team, since it contradicts their own docs.
        adpConsentApi.disableAutoPageCapture?.()
        adpConsentApi.load({
            name: EVENT_GENERATOR,
            id_provider: "appkey",
            id: window.forge_client_id,
            line_name: EVENT_GENERATOR,
        })
    } catch (error) {
        Sentry.captureException(error)
    }
}

/**
 * The Autodesk access token for this page load, fetched at most once.
 *
 * Every call to getAutodeskAccessTokenSilently() can start a hidden-iframe silent-auth
 * flow, and concurrent or back-to-back flows race each other -- the loser 404s on
 * /as/flows/<id>. Consent is read more than once per page load (initial check, each reopen
 * of the dialog), so the promise is shared across all of them.
 * @returns the access token, or null when one can't be retrieved silently
 */
const getConsentAccessToken = (): Promise<string | null> => {
    if (!autodeskAccessTokenPromise) {
        autodeskAccessTokenPromise = getAutodeskAccessTokenSilently(window.autodesk_oauth_client_id, window.autodesk_oauth_env)
    }
    return autodeskAccessTokenPromise
}

/**
 * Reads the current user's consent preferences from the ADP consent service.
 *
 * Always uses the "Named User" flow: consent is only ever read or written for users who
 * signed in through Autodesk Identity, so their 3-legged access token identifies the
 * profile.autodesk.com account the preferences belong to. Returns null (fail closed --
 * callers treat it as "no decision to apply") when the API never initialized, no token
 * could be retrieved silently, or the service call failed.
 * @returns the consent result, or null when it couldn't be read
 */
const loadConsentPreferences = async (): Promise<any | null> => {
    if (!adpConsentApi) {
        return null
    }
    try {
        const autodeskAccessToken = await getConsentAccessToken()
        if (!autodeskAccessToken) {
            return null
        }
        return await adpConsentApi.loadUserConsentPreferences(autodeskAccessToken)
    } catch (error) {
        Sentry.captureException(error)
        return null
    }
}

/**
 * Whether the given consent result opts into the tracking category we gate on.
 *
 * `userConsentPreferences` is a per-category array (consentId + optIn + lastUpdated) --
 * see TRACKING_CONSENT_ID for which category is read. Absent entirely (not an empty array)
 * when the user hasn't decided yet, so this treats a missing array the same as the
 * category being missing from it: not opted in.
 * @param consentResult the result from loadConsentPreferences()
 * @returns whether tracking cookies are allowed
 */
const isTrackingOptedIn = (consentResult: any): boolean => consentResult.userConsentPreferences?.find((preference: any) => preference.consentId === TRACKING_CONSENT_ID)?.optIn === true

/**
 * Deletes the cookies written by the tracking integrations, used when consent is
 * declined/revoked. Shutting FullStory/Amplitude down stops further collection but leaves
 * their existing cookies in place.
 */
const clearTrackingCookies = (): void => {
    document.cookie
        .split(";")
        .map(cookie => cookie.split("=")[0].trim())
        .filter(name => TRACKING_COOKIE_PREFIXES.some(prefix => name.startsWith(prefix)))
        .forEach(name => deleteCookie(name))
}

/**
 * Starts or stops the tracking-cookie-dependent integrations to match a consent decision.
 *
 * FullStory capture, Amplitude's deferred-init start, and Intercom identification are all
 * tracked independently of the other integrations (see fullStoryCaptureState/
 * amplitudeTrackingStarted/intercomIdentified) since they default to "not started"/
 * "anonymous" rather than "granted", so they're always re-evaluated here regardless of
 * whether trackingConsentGranted actually changed.
 * @param granted whether tracking cookies are allowed
 */
const applyTrackingConsent = (granted: boolean): void => {
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] applyTrackingConsent", { granted, trackingConsentGranted, fullStoryCaptureState, amplitudeTrackingStarted, intercomIdentified })
    if (granted) {
        startFullStoryCapture()
        startAmplitudeTracking()
        identifyIntercomUser()
    } else {
        stopFullStoryCapture()
        anonymizeIntercomUser()
    }
    if (trackingConsentGranted === granted) {
        return
    }
    trackingConsentGranted = granted
    if (granted) {
        setAmplitudeOptOut(false)
    } else {
        setAmplitudeOptOut(true)
        clearTrackingCookies()
    }
}

/**
 * Wraps the ADP SDK's consent-write method so we learn the user's decision the moment
 * they close the built-in consent dialog, rather than on the next page load.
 *
 * The ADP SDK exposes no consent-changed event. It does document
 * adp.setCurrentUserConsentPreferences() as the call an integration makes when rendering
 * its *own* consent dialog, and the SDK's built-in dialog -- the one we use -- goes through
 * the same API to persist the choice. Wrapping it is therefore the closest thing to an
 * event the SDK offers. Best effort: if a future SDK version writes consent another way
 * the wrapper simply never fires and the next page load picks up the decision instead.
 */
const installConsentDecisionHook = (): void => {
    const adp = adpConsentApi
    if (!adp || consentDecisionHookInstalled || typeof adp.setCurrentUserConsentPreferences !== "function") {
        return
    }
    const setPreferences = adp.setCurrentUserConsentPreferences.bind(adp)
    consentDecisionHookInstalled = true
    adp.setCurrentUserConsentPreferences = (preferences: any): unknown => {
        // TODO(WA-9573): remove before merging to master.
        console.warn("[adpConsent debug] setCurrentUserConsentPreferences wrapper fired", preferences)
        const writeResult = setPreferences(preferences)
        // Apply only once the SDK's own write settles, so we never enable tracking off the
        // back of a write that failed.
        Promise.resolve(writeResult)
            .then(() => {
                const trackingPreference = Array.isArray(preferences) ? preferences.find((preference: any) => preference?.consentId === TRACKING_CONSENT_ID) : null
                // TODO(WA-9573): remove before merging to master.
                console.warn("[adpConsent debug] setCurrentUserConsentPreferences write settled", trackingPreference)
                if (typeof trackingPreference?.optIn === "boolean") {
                    applyTrackingConsent(trackingPreference.optIn)
                }
            })
            .catch(error => Sentry.captureException(error))
        return writeResult
    }
}

/**
 * Applies the FullStory/Amplitude/Intercom consent decision appropriate for this user, now
 * that login has finished and we know whether they're an Autodesk user.
 *
 * Autodesk users have an ADP consent decision to look up and apply (fail-closed: capture/
 * identification stays off if there's no decision yet, until they visit Privacy Settings and
 * make one). Non-Autodesk users have no Autodesk identity for ADP to check consent against,
 * so nothing gates their tracking -- it starts immediately. When cookie management is off,
 * FullStory and Amplitude already started unconditionally at page load (see
 * rmbx_minimal.html); Intercom identification has no template-level equivalent, so it's
 * applied here unconditionally too, to match that same "flag off = no gating" behavior.
 * @param isAutodeskUser whether this user authenticates via Autodesk Identity
 * @param userInfo the current user, used to identify them in Intercom once allowed
 */
export const applyStoredConsent = async (isAutodeskUser: boolean, userInfo: iCurrentUser | null): Promise<void> => {
    currentUserInfo = userInfo
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] applyStoredConsent", { isAutodeskUser, cookieManagementEnabled: isCookieManagementEnabled(), hasUserInfo: !!userInfo })
    if (!isCookieManagementEnabled()) {
        identifyIntercomUser()
        return
    }
    if (!isAutodeskUser) {
        startFullStoryCapture()
        startAmplitudeTracking()
        identifyIntercomUser()
        return
    }
    initializeAdpConsentApi()
    const consentResult = await loadConsentPreferences()
    // TODO(WA-9573): remove before merging to master.
    console.warn("[adpConsent debug] stored consent result", consentResult)
    applyTrackingConsent(consentResult ? isTrackingOptedIn(consentResult) : false)
}

/**
 * Opens the ADP built-in consent dialog (e.g. from the "Privacy Settings" nav item) and
 * applies whatever the user decides, including revoking consent mid-session.
 *
 * Refreshes the tracker's consent preferences beforehand so the dialog reflects the user's
 * current choices rather than stale state from page load. No-ops if the ADP consent API
 * isn't available (cookie management off, non-Autodesk user, or the ADP SDK didn't load).
 */
export const openTrackingConsentDialog = async (): Promise<void> => {
    initializeAdpConsentApi()
    if (!adpConsentApi) {
        return
    }
    installConsentDecisionHook()
    const consentResult = await loadConsentPreferences()
    if (consentResult) {
        applyTrackingConsent(isTrackingOptedIn(consentResult))
    }
    adpConsentApi.showUserConsentDialog()
}
